import { createElement, useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from "react";
import { Link, useParams } from "react-router";
import type { ActorDTO, DecisionDTO, RunDTO, RunEventDTO, TaskDetail } from "@roomate/core";
import { ApiError, useActors, useAnswer, useMe, useRun, useRunLessons, useStopRun, useTask } from "../api.ts";
import { useLive } from "../live.tsx";
import { LessonGovernButtons } from "../components/CorrectionRules.tsx";
import { isCorrectionLesson, lessonAnchor, lessonRef, lessonScope } from "../lessons.ts";
import { Action, Avatar, Bento, Check, Disclosure, Page, PageHeader, Pill, Stat, Switch, Tile } from "../calm.tsx";
import { Ago, cx, ErrorNote, formatMin } from "../ui.tsx";
import { AbandonRunControl, canAbandonRun } from "../components/AbandonRun.tsx";
import { RunRuntime } from "../components/RunRuntime.tsx";
import { GuardNoteRow } from "../components/GuardNote.tsx";
import { parseGuardNote } from "../lib/guard.ts";
import { deliverableRef, rawOutput, runWhere, steerBlockedReason } from "../lib/run-outcome.ts";
import { ChiefNote } from "../components/ChiefNote.tsx";
import { NEEDS_RATIONALE } from "./Inbox.tsx";
import { runSkillsView } from "../lib/skills-ui.ts";

export const RUN_STATUS_LABEL: Record<RunDTO["status"], string> = {
  starting: "Starting",
  running: "Running",
  succeeded: "Finished",
  failed: "Failed",
  stopped: "Stopped",
  budget_exceeded: "Budget hit",
  superseded: "Superseded",
};

export function runStatusClass(status: RunDTO["status"]): string {
  switch (status) {
    case "running":
    case "starting":
      return "text-signal";
    case "succeeded":
      return "text-ok";
    case "failed":
    case "budget_exceeded":
      return "text-high";
    case "stopped":
    case "superseded":
      return "text-ink-3";
  }
}

export const RUN_PILL_TONE: Record<RunDTO["status"], "neutral" | "live" | "signal" | "danger" | "done"> = {
  starting: "live",
  running: "live",
  succeeded: "done",
  failed: "danger",
  budget_exceeded: "danger",
  stopped: "neutral",
  superseded: "neutral",
};

export const RUN_STAT_TONE: Record<RunDTO["status"], "signal" | "ok" | "danger" | undefined> = {
  starting: "signal",
  running: "signal",
  succeeded: "ok",
  failed: "danger",
  budget_exceeded: "danger",
  stopped: undefined,
  superseded: undefined,
};

export function money(usd: number): string {
  return usd >= 1 ? `$${usd.toFixed(2)}` : `$${usd.toFixed(3)}`;
}

/** Merges the fetched transcript with lines that arrived live, by seq. */
export function useRunTranscript(id: string) {
  const q = useRun(id);
  const live = useLive();
  const events = useMemo(() => {
    const bySeq = new Map<number, RunEventDTO>();
    for (const e of q.data?.events ?? []) bySeq.set(e.seq, e);
    for (const e of live.runEvents[id] ?? []) bySeq.set(e.seq, e);
    return [...bySeq.values()].sort((a, b) => a.seq - b.seq);
  }, [q.data?.events, live.runEvents[id], id]);
  const run = live.runs[id] && q.data && live.runs[id].lastActivityAt >= q.data.run.lastActivityAt ? live.runs[id] : q.data?.run;
  return { run, events, error: q.error, loading: q.isLoading };
}

/** Keyed on the run id so run-to-run navigation (the Fallback badge) remounts and resets transcript filter state. */
export function runPageElement(id: string): ReactElement {
  return createElement(RunPageBody, { key: id, id });
}

export function RunPage() {
  const { id = "" } = useParams();
  return runPageElement(id);
}

function RunPageBody({ id }: { id: string }) {
  const { run, events, error, loading } = useRunTranscript(id);
  const actors = useActors().data;
  const me = useMe().data!;
  const stop = useStopRun();
  const task = useTask(run?.taskId ?? "");
  const [follow, setFollow] = useState(true);
  const [showThinking, setShowThinking] = useState(false);
  const [subagent, setSubagent] = useState<string | "all" | "main">("main");
  const [tab, setTab] = useState<"transcript" | "raw">("transcript");
  const bottom = useRef<HTMLDivElement>(null);

  const rawPane = useRef<HTMLPreElement>(null);

  useEffect(() => {
    if (!follow) return;
    // The raw pane scrolls inside itself; the transcript scrolls the page.
    if (tab === "raw" && rawPane.current) rawPane.current.scrollTop = rawPane.current.scrollHeight;
    else bottom.current?.scrollIntoView({ block: "end" });
  }, [events.length, follow, tab]);

  if (error)
    return (
      <Page>
        <ErrorNote error={error} />
      </Page>
    );
  if (loading || !run)
    return (
      <Page>
        <p className="text-ink-3">Loading…</p>
      </Page>
    );

  const active = run.status === "running" || run.status === "starting";
  const agent = actors?.find((a) => a.id === run.agentId);
  const subagents = [...new Set(events.map((e) => e.parent).filter((p): p is string => p !== null))];
  const visible = events.filter((e) => (showThinking || e.kind !== "thinking") && (subagent === "all" || (subagent === "main" ? e.parent === null : e.parent === subagent)));
  const toolNames = new Map(events.filter((e) => e.kind === "tool_use").map((e) => [String((e.data as any)?.id ?? ""), e.tool ?? ""]));
  const where = runWhere(run);
  const steerReason = active ? steerBlockedReason(run) : null;
  const canStop = active && me.kind === "human" && me.access !== "viewer";

  return (
    <Page wide>
      <PageHeader
        crumbs={[
          { label: "Project", to: `/projects/${run.projectId}` },
          { label: task.data ? `#${task.data.task.number} ${task.data.task.title}` : "Task", to: `/tasks/${run.taskId}` },
          { label: "Run" },
        ]}
        title={
          <>
            {agent?.name ?? run.agentId.slice(0, 10)} {active && <span className="live-dot ml-2 inline-block" />}
          </>
        }
        meta={
          task.data && (
            <span className="flex flex-wrap items-center gap-2" data-testid="run-goal">
              <span className="text-ink-2">{task.data.task.title}</span>
              <Pill testId="run-deliverable">{task.data.task.deliverable ?? "code"}</Pill>
              <Pill tone={task.data.task.risk.tier === "low" ? "neutral" : "signal"} testId="run-risk">
                {task.data.task.risk.tier} risk
              </Pill>
            </span>
          )
        }
        status={
          <span className="inline-flex items-center gap-2">
            <Pill tone={RUN_PILL_TONE[run.status]} testId="run-status">
              {RUN_STATUS_LABEL[run.status]}
            </Pill>
            <RunRuntime run={run} link />
          </span>
        }
        actions={
          (canStop || canAbandonRun(me, run)) && (
            <span className="flex items-center gap-2">
              {canStop && (
                <Action danger onClick={() => stop.mutate(run.id)} disabled={stop.isPending || run.stopRequested}>
                  {run.stopRequested ? "Stopping…" : "Stop"}
                </Action>
              )}
              <AbandonRunControl me={me} run={run} />
            </span>
          )
        }
      />
      <ErrorNote error={stop.error} />

      <Bento className="mt-8">
        {task.data && <OutcomeTile detail={task.data} me={me} steerReason={steerReason} />}
        <Tile span={6}>
          <div className="flex flex-wrap items-center gap-6">
            <Avatar name={agent?.name ?? "Agent"} kind={agent?.kind ?? "agent"} size={48} live={active} />
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
              <Stat value={RUN_STATUS_LABEL[run.status]} label="status" tone={RUN_STAT_TONE[run.status]} />
              <Stat value={money(run.costUsd)} label="cost" />
            </div>
          </div>
          {run.error && <p className="mt-4 rounded-2xl bg-high/10 px-3.5 py-2.5 text-[13.5px] text-high">{run.error}</p>}
          <dl className="mt-5 flex flex-wrap gap-x-6 gap-y-2 border-t border-rule/60 pt-4 text-[13px]" data-testid="run-stats">
            <StatRow label="cost" value={money(run.costUsd)} />
            <StatRow label="tokens" value={`${(run.inputTokens + run.cacheReadTokens).toLocaleString()} in · ${run.outputTokens.toLocaleString()} out`} />
            <StatRow label="turns" value={String(run.numTurns)} />
            <StatRow label="duration" value={run.finishedAt ? formatMin((run.finishedAt - run.startedAt) / 60_000) : <Ago ts={run.startedAt} />} />
            {run.branch && <StatRow label="branch" value={run.branch} />}
            {run.model && <StatRow label="model" value={run.model} />}
            <StatRow label={where.label} value={where.value} />
          </dl>
        </Tile>
      </Bento>

      <RunSkillsSection run={run} events={events} />

      <LessonsGiven runId={run.id} projectId={run.projectId} />

      <div className="mt-6 flex flex-wrap items-center gap-4 text-[13px] text-ink-2">
        <Switch checked={follow} onChange={setFollow} label="Follow" />
        <Switch checked={showThinking} onChange={setShowThinking} label="Thinking" />
        {subagents.length > 0 && (
          <select className="rounded-full bg-ink/[0.05] px-3 py-1 text-[13px]" value={subagent} onChange={(e) => setSubagent(e.target.value)} aria-label="Conversation">
            <option value="main">main conversation</option>
            <option value="all">everything</option>
            {subagents.map((s) => (
              <option key={s} value={s}>
                subagent {toolNames.get(s) || s.slice(0, 8)}
              </option>
            ))}
          </select>
        )}
        <span className="ml-auto text-ink-3">{visible.length} lines</span>
      </div>

      <div className="mt-4 flex gap-1" role="group" aria-label="Run output">
        {(
          [
            ["transcript", "Transcript"],
            ["raw", "Raw output"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            aria-pressed={tab === key}
            onClick={() => setTab(key)}
            data-testid={`run-tab-${key}`}
            className={cx("rounded-full px-3.5 py-1 text-[13px] transition-colors", tab === key ? "bg-ink text-paper" : "bg-ink/[0.05] text-ink-2 hover:bg-ink/[0.09]")}
          >
            {label}
          </button>
        ))}
      </div>

      <Bento className="mt-3">
        <Tile span={6}>
          {tab === "transcript" ? (
            <ol className="space-y-1.5 text-[13.5px]" aria-live="polite" data-testid="transcript">
              {buildTranscriptRows(visible).map((row) =>
                row.kind === "tool" ? <ToolBlock key={row.key} use={row.use} result={row.result} /> : <TranscriptLine key={row.key} e={row.e} />,
              )}
              {visible.length === 0 && <li className="text-ink-3">Waiting for the first line…</li>}
            </ol>
          ) : (
            <pre ref={rawPane} className="max-h-[70vh] overflow-auto whitespace-pre-wrap break-words font-mono text-[12.5px] leading-relaxed text-ink-2" data-testid="run-raw">
              {rawOutput(visible) || "Waiting for the first line…"}
            </pre>
          )}
        </Tile>
      </Bento>
      <div ref={bottom} />
    </Page>
  );
}

/** What this run is for: the criteria and how far they are met, the decisions waiting on a person, and where the work landed. */
export function OutcomeTile({ detail, me, steerReason }: { detail: TaskDetail; me: Pick<ActorDTO, "id" | "kind" | "access">; steerReason: string | null }) {
  const t = detail.task;
  const last = detail.evidence.at(-1);
  const open = detail.decisions.filter((d) => d.status === "open");
  const ref = deliverableRef(t, last);
  return (
    <Tile span={6} title="Outcome" aside={<Link to={`/tasks/${t.id}`} className="text-ink-2 underline decoration-rule hover:text-ink">Open task</Link>} testId="run-outcome">
      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <h3 className="flex items-baseline justify-between text-[12.5px] text-ink-3">
            <span>Done when</span>
            {last && (
              <span className={last.result.verdict === "pass" ? "text-ok" : "text-high"} data-testid="run-verdict">
                last check {last.result.verdict}
              </span>
            )}
          </h3>
          {t.acceptanceCriteria.length === 0 ? (
            <p className="mt-2 text-[13.5px] text-ink-3">No acceptance criteria yet.</p>
          ) : (
            <ul className="mt-1 divide-y divide-rule/60" data-testid="run-criteria">
              {t.acceptanceCriteria.map((c) => {
                // The hub's verdict, not the builder's claim; the claim is only a fallback for evidence without one.
                const v = (last?.result.acceptance ?? last?.payload.acceptance)?.find((a) => a.criterionId === c.id);
                return (
                  <Check key={c.id} state={v && v.status !== "missing" ? v.status : "pending"} detail={v?.evidence || undefined}>
                    {c.text}
                  </Check>
                );
              })}
            </ul>
          )}
        </div>
        <div className="space-y-5">
          <div>
            <h3 className="text-[12.5px] text-ink-3">Waiting on a person</h3>
            {open.length === 0 ? (
              <p className="mt-2 text-[13.5px] text-ink-3" data-testid="run-no-decisions">
                Nothing is waiting on anyone.
              </p>
            ) : (
              <ul className="mt-2 space-y-3" data-testid="run-decisions">
                {open.map((d) => (
                  <OutcomeDecision key={d.id} decision={d} canAnswer={me.kind === "human" && me.access !== "viewer" && (me.access === "admin" || d.assigneeId === me.id)} />
                ))}
              </ul>
            )}
          </div>
          <div>
            <h3 className="text-[12.5px] text-ink-3">Deliverable · {t.deliverable ?? "code"}</h3>
            <p className="mt-2 break-all font-mono text-[12.5px] text-ink-2" data-testid="run-deliverable-ref">
              {ref === null ? (
                <span className="font-sans text-ink-3">Nothing delivered yet.</span>
              ) : /^https?:\/\//i.test(ref) ? (
                <a href={ref} target="_blank" rel="noreferrer noopener" className="underline">
                  {ref}
                </a>
              ) : (
                ref
              )}
            </p>
          </div>
          {steerReason && (
            <div>
              <h3 className="text-[12.5px] text-ink-3">Steer</h3>
              <p className="mt-2 text-[13.5px] text-ink-2" data-testid="run-steer-reason">
                {steerReason}
              </p>
            </div>
          )}
        </div>
      </div>
    </Tile>
  );
}

/** An open decision with its options as buttons. An option that needs a reason is answered from the inbox. */
function OutcomeDecision({ decision, canAnswer }: { decision: DecisionDTO; canAnswer: boolean }) {
  const answer = useAnswer();
  return (
    <li data-testid="run-decision">
      <p className="text-[13.5px] font-medium">{decision.question}</p>
      <ChiefNote note={decision.chiefNote} className="mt-2" />
      {canAnswer ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {decision.options.map((o) =>
            NEEDS_RATIONALE.has(o.id) || decision.kind === "risk_acceptance" ? (
              <Link key={o.id} to={`/inbox?decision=${decision.id}`} className="rounded-full bg-ink/[0.05] px-3 py-1 text-[13px] hover:bg-ink/[0.09]" data-testid="run-decision-option">
                {o.label}
              </Link>
            ) : (
              <button
                key={o.id}
                type="button"
                disabled={answer.isPending}
                onClick={() => answer.mutate({ id: decision.id, optionId: o.id })}
                data-option={o.id}
                data-testid="run-decision-option"
                className={cx("rounded-full px-3 py-1 text-[13px] disabled:opacity-50", decision.recommendation === o.id ? "bg-ink text-paper" : "bg-ink/[0.05] hover:bg-ink/[0.09]")}
              >
                {o.label}
              </button>
            ),
          )}
        </div>
      ) : (
        <p className="mt-1 text-[12.5px] text-ink-3">
          Waiting on someone else.{" "}
          <Link to={`/inbox?decision=${decision.id}`} className="underline">
            Open it
          </Link>
        </p>
      )}
      <ErrorNote error={answer.error} />
    </li>
  );
}

/** The lessons this run was given before it started. Hidden when it was given none. */
function LessonsGiven({ runId, projectId }: { runId: string; projectId: string }) {
  const q = useRunLessons(runId);
  // A 404 means this hub has no lesson routes yet (older hub). That is not a failure of this run.
  if (q.error instanceof ApiError && q.error.status === 404) return null;
  if (q.error)
    return (
      <Bento className="mt-6">
        <Tile span={6} title="Lessons given">
          <ErrorNote error={q.error} />
        </Tile>
      </Bento>
    );
  const lessons = q.data ?? [];
  if (lessons.length === 0) return null;
  return (
    <Bento className="mt-6">
      <Tile span={6} title="Lessons given" aside={`${lessons.length}`} testId="run-lessons-given">
        <ul className="divide-y divide-rule/60">
          {lessons.map((l) => (
            <li key={l.id} className="flex items-start justify-between gap-3 py-3 first:pt-0 last:pb-0" data-lesson-id={l.id}>
              <div className="min-w-0 flex-1">
              <p className="whitespace-pre-wrap break-words text-[13.5px] leading-relaxed">{l.text}</p>
              <p className="mt-1 flex flex-wrap items-center gap-x-2 text-[12px] text-ink-3">
                <span className="font-mono">{lessonRef(l.id)}</span>
                {isCorrectionLesson(l) && (
                  <Pill tone="signal">
                    <span data-testid="run-lesson-correction">correction rule</span>
                  </Pill>
                )}
                <span aria-hidden>·</span>
                <span>{lessonScope(l.areas)}</span>
                <span aria-hidden>·</span>
                <Link to={`/projects/${projectId}/lessons#${lessonAnchor(l.id)}`} className="text-ink-2 underline decoration-rule hover:text-ink">
                  on the Lessons page
                </Link>
              </p>
              </div>
              {isCorrectionLesson(l) && <LessonGovernButtons lesson={l} testIdPrefix="run-lesson" />}
            </li>
          ))}
        </ul>
      </Tile>
    </Bento>
  );
}

/** Which library skills this run was given and which of them Claude Code reported loading. Renders nothing for a run with no skills. */
export function RunSkillsSection({ run, events }: { run: Pick<RunDTO, "runtime">; events: readonly RunEventDTO[] }) {
  const view = runSkillsView(run, events);
  if (view.state === "none") return null;
  return (
    <Bento className="mt-6">
      <Tile span={6} title="Skills">
        <div data-testid="run-skills">
          {view.state === "unsupported" ? (
            <p className="text-[13.5px] text-ink-3" data-testid="run-skills-unsupported">skills not supported on this runtime</p>
          ) : (
            <>
              <p className="text-[13px] text-ink-2" data-testid="run-skills-sandbox">
                {view.sandboxed ? "Sandboxed run: approved hooks were kept." : "Not sandboxed: every hook was stripped."}
              </p>
              <ul className="mt-3 space-y-1.5 text-[13.5px]">
                {view.skills.map((s) => (
                  <li key={s.name} className="flex flex-wrap items-center gap-2" data-testid={`run-skill-${s.name}`}>
                    <span className="font-medium">{s.name}</span>
                    {s.version !== null && <span className="text-[12px] text-ink-3">v{s.version}</span>}
                    <Pill tone={s.loaded ? "live" : "neutral"} testId={`run-skill-state-${s.name}`}>{s.loaded ? "loaded" : "delivered (load not reported)"}</Pill>
                    <span className="text-[12px] text-ink-3" data-testid={`run-skill-hooks-${s.name}`}>
                      {s.keptHookIds.length ? `hooks kept: ${s.keptHookIds.join(", ")}` : "no hooks kept"}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </Tile>
    </Bento>
  );
}

function StatRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-ink-3">{label}</dt>
      <dd className="mt-0.5 truncate text-ink-2">{value}</dd>
    </div>
  );
}

type TranscriptRow = { kind: "tool"; key: string; use: RunEventDTO; result: RunEventDTO | null } | { kind: "line"; key: string; e: RunEventDTO };

/** Pairs each tool_use with the tool_result that answers it, by toolUseId, so they render as one block. */
function buildTranscriptRows(events: RunEventDTO[]): TranscriptRow[] {
  const resultByUseId = new Map<string, RunEventDTO>();
  for (const e of events) {
    const id = e.kind === "tool_result" ? (e.data as any)?.toolUseId : null;
    if (typeof id === "string" && id) resultByUseId.set(id, e);
  }
  const consumed = new Set<number>();
  const rows: TranscriptRow[] = [];
  for (const e of events) {
    if (e.kind === "tool_use") {
      const id = String((e.data as any)?.id ?? "");
      const result = id ? (resultByUseId.get(id) ?? null) : null;
      if (result) consumed.add(result.seq);
      rows.push({ kind: "tool", key: `tool-${e.seq}`, use: e, result });
    } else if (e.kind === "tool_result" && consumed.has(e.seq)) {
      continue;
    } else {
      rows.push({ kind: "line", key: `line-${e.seq}`, e });
    }
  }
  return rows;
}

/** A tool call and its result as one collapsible block. Long successful results start folded; short or failed ones start open. */
function ToolBlock({ use, result }: { use: RunEventDTO; result: RunEventDTO | null }) {
  const resultText = result?.text ?? "";
  const isError = Boolean((result?.data as any)?.isError);
  const startOpen = !result || isError || resultText.length <= 400;
  return (
    <li>
      <Disclosure
        defaultOpen={startOpen}
        className={cx("rounded-2xl px-3 py-2", isError ? "bg-high/[0.08]" : "bg-ink/[0.03]")}
        summary={
          <span className="flex min-w-0 flex-1 items-center gap-2">
            <span className="shrink-0 font-medium text-ink">{use.tool?.replace(/^mcp__roomate__/, "roomate:")}</span>
            <span className="min-w-0 truncate text-ink-3" title={use.text}>
              {use.text}
            </span>
            <span className="ml-auto shrink-0 pl-2 text-[11px] text-ink-3">{!result ? "running…" : isError ? <span className="text-high">error</span> : "ok"}</span>
          </span>
        }
      >
        <p className={cx("whitespace-pre-wrap break-words font-mono text-[12.5px]", isError ? "text-high" : "text-ink-2")}>{resultText || "(empty)"}</p>
      </Disclosure>
    </li>
  );
}

function TranscriptLine({ e }: { e: RunEventDTO }) {
  const guard = parseGuardNote(e);
  if (guard) return <GuardNoteRow note={guard} />;
  const role = (e.data as any)?.role;
  switch (e.kind) {
    case "text":
      return (
        <li className={cx("whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2.5", role === "assistant" ? "bg-ink/[0.03]" : "text-ink-2")}>{e.text}</li>
      );
    case "thinking":
      return <li className="whitespace-pre-wrap break-words border-l-2 border-rule pl-3 italic text-ink-3">{e.text}</li>;
    case "tool_result":
      return <li className={cx("whitespace-pre-wrap break-words pl-5 font-mono text-[12.5px]", (e.data as any)?.isError ? "text-high" : "text-ink-3")}>{e.text || "(empty)"}</li>;
    case "init":
      return <li className="text-ink-3">⚙ {e.text}</li>;
    case "system":
    case "note":
      return <li className="text-ink-3">· {e.text}</li>;
    case "stderr":
      return <li className="whitespace-pre-wrap break-words font-mono text-high/80">{e.text}</li>;
    case "result":
      return (
        <li className="mt-3 rounded-2xl bg-ink/[0.03] px-3.5 py-2.5">
          <span className="text-[11px] text-ink-3">result · {(e.data as any)?.subtype}</span>
          <p className="mt-1 whitespace-pre-wrap break-words">{e.text}</p>
        </li>
      );
  }
}
